import { ChevronRight } from "@mui/icons-material";
import { Link as MuiLink } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import qs from "qs";
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom";
import {
  PixAreaSkeleton,
  ScreenGreeting,
  TransactionListSkeleton,
} from "../../../Components/Molecules";
import { GridPagination } from "../../../Components/Molecules/GridHistory";
import { PixShortcutMenu } from "../../../Components/Molecules/PixShortcutMenu";
import { useAuth } from "../../../Contexts/AuthContext";
import { IB_ACTIVE_ACCOUNT_CHANGED_EVENT } from "../../../Constants/accountTypes";
import {
  BAAS_STATEMENT_PAGE_SIZE,
  MAX_STATEMENT_ITEMS,
} from "../../../Hooks/Transactions";
import { usePixTransactionsData } from "../../../Hooks/UsePix";
import { useFilter } from "../../../Hooks/useFilter";
import { ExtractDayTimeline } from "../Extract/ExtractDayTimeline";
import { ExtractPeriodSummary } from "../Extract/ExtractPeriodSummary";
import { ExtractTimelineToggleAll } from "../Extract/ExtractTimelineToggleAll";
import { paginateExtractDayGroups } from "../Extract/extractFilters";
import { PixFilterBar } from "./PixFilterBar";
import { PixQrReceiveModal } from "./PixSales/PixQrReceiveModal";
import { PixTransferModal } from "./PixTransfers/PixTransferModal";
import {
  applyPixClientFilters,
  filtersFromPixFilterObj,
  flattenTransactionDayGroups,
  pixFilterObjFromFilters,
  regroupTransactionsByDay,
} from "./pixStatementFilters";

const PIX_PATH = "/pix";
const DEFAULT_DAYS_PER_PAGE = 10;
const EMPTY_SUB_ACCOUNTS = [];

export const PixArea = () => {
  const history = useHistory();
  const location = useLocation();
  const parsedQS = qs.parse(location.search.replace("?", ""));
  const { hasPermission } = useAuth();
  const canPayPix = hasPermission("paypix");
  const canReceivePix = hasPermission("receivepix");
  const [qrReceiveModal, setQrReceiveModal] = useState(false);
  const [transferModal, setTransferModal] = useState(false);
  const [filters, setFilters] = useState(null);
  const [page, setPage] = useState(1);
  const [daysPerPage, setDaysPerPage] = useState(DEFAULT_DAYS_PER_PAGE);
  const [allExpanded, setAllExpanded] = useState(true);
  const {
    state: transactionsData,
    handleGET: fetchTransactionsGET,
    loading: transactionsLoading,
  } = usePixTransactionsData();

  const { getFilterObj, handleApplyFilters } = useFilter();
  const pixFilterObj = getFilterObj("pixStatement");

  useEffect(() => {
    setFilters((prev) => ({
      ...filtersFromPixFilterObj(pixFilterObj),
      searchQuery: prev?.searchQuery ?? "",
    }));
  }, [
    pixFilterObj.start_date,
    pixFilterObj.end_date,
    pixFilterObj.start_amount,
    pixFilterObj.end_amount,
    pixFilterObj.productId,
    pixFilterObj.sub_account_id,
  ]);

  const pixApiFilterKey = [
    pixFilterObj.start_date,
    pixFilterObj.end_date,
    pixFilterObj.start_amount,
    pixFilterObj.end_amount,
    pixFilterObj.sub_account_id,
  ];

  const buildPixStatementQuery = useCallback(() => {
    const { productId: _productId, ...pixApiFilterObj } = pixFilterObj;

    // Same Extrato feed: GET /v1/ib/accounts/statements → BaaS account statements.
    // Pix-only filter is client-side (applyPixClientFilters / isPixStatementItem).
    return {
      page: 1,
      page_size: BAAS_STATEMENT_PAGE_SIZE,
      fetch_all: true,
      max_items: MAX_STATEMENT_ITEMS,
      ...pixApiFilterObj,
    };
  }, [pixFilterObj]);

  const refreshPixForActiveAccount = useCallback(() => {
    fetchTransactionsGET(buildPixStatementQuery());
    // handleGET from useTransactionsData is not memoized; keep stable on filter changes only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, pixApiFilterKey);

  useEffect(() => {
    refreshPixForActiveAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, pixApiFilterKey);

  useEffect(() => {
    const onActiveAccountChanged = () => {
      refreshPixForActiveAccount();
    };
    window.addEventListener(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, onActiveAccountChanged);
    return () => {
      window.removeEventListener(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, onActiveAccountChanged);
    };
  }, [refreshPixForActiveAccount]);

  useEffect(() => {
    if (String(parsedQS.newTransfer) === "true" && canPayPix) {
      setTransferModal(true);
      history.replace(PIX_PATH);
    } else if (String(parsedQS.qrReceive) === "true" && canReceivePix) {
      setQrReceiveModal(true);
      history.replace(PIX_PATH);
    }
    // Only react to the landing URL (e.g. transferencias hub ?newTransfer=true).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flatItems = useMemo(
    () => flattenTransactionDayGroups(transactionsData?.items),
    [transactionsData?.items],
  );

  const filteredFlatItems = useMemo(
    () => applyPixClientFilters(flatItems, filters ?? {}),
    [flatItems, filters],
  );

  const displayGroups = useMemo(
    () => regroupTransactionsByDay(filteredFlatItems),
    [filteredFlatItems],
  );

  const paginatedDayGroups = useMemo(
    () => paginateExtractDayGroups(displayGroups, page, daysPerPage),
    [displayGroups, page, daysPerPage],
  );

  const totalDayGroups = displayGroups.length;
  const currentPageDayCount = paginatedDayGroups.length;

  const timelineDayKey = useMemo(
    () =>
      paginatedDayGroups
        .map((group) => (group ? Object.keys(group)[0] : null))
        .filter(Boolean)
        .join("|"),
    [paginatedDayGroups],
  );

  useEffect(() => {
    setAllExpanded(true);
  }, [timelineDayKey, page]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalDayGroups / daysPerPage) || 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [totalDayGroups, daysPerPage, page]);

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
    handleApplyFilters("pixStatement", pixFilterObjFromFilters(nextFilters));
  };

  const handleDaysPerPageChange = (value) => {
    setDaysPerPage(value);
    setPage(1);
  };

  const hasItems = (transactionsData?.items?.length ?? 0) > 0;
  const isInitialLoading = (transactionsLoading.GET && !hasItems) || filters === null;

  return (
    <div className="w-full">
      <ScreenGreeting
        title={intl.get("pix.title")}
        subTitle={intl.get("pix.subtitle")}
      />
      <PixShortcutMenu
        onOpenQrReceive={canReceivePix ? () => setQrReceiveModal(true) : undefined}
        onOpenTransfer={canPayPix ? () => setTransferModal(true) : undefined}
      />

      {isInitialLoading ? (
        <PixAreaSkeleton />
      ) : (
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="m-0 text-sm font-bold text-brand-dark dark:text-gray-100">
              {intl.get("phrases.myExtract")}
            </h2>
            {hasPermission("statementsview") ? (
              <MuiLink
                component={RouterLink}
                underline="none"
                to="/extrato"
                className="inline-flex items-center gap-0.5 text-xs font-semibold text-[#D3A971] no-underline transition hover:opacity-70"
              >
                {intl.get("common.seeMore")}
                <ChevronRight sx={{ fontSize: 16 }} />
              </MuiLink>
            ) : null}
          </div>

          <div className="mb-4">
            <PixFilterBar
              filters={filters}
              onFiltersChange={handleFiltersChange}
              filteredItems={filteredFlatItems}
              subAccounts={EMPTY_SUB_ACCOUNTS}
            />
            <ExtractPeriodSummary items={filteredFlatItems} className="mt-4" />
          </div>

          <div className="w-full">
            {transactionsLoading.GET ? (
              <TransactionListSkeleton rows={8} />
            ) : (
              <>
                {paginatedDayGroups.length > 0 && (
                  <ExtractTimelineToggleAll
                    allExpanded={allExpanded}
                    onToggle={() => setAllExpanded((prev) => !prev)}
                  />
                )}
                <ExtractDayTimeline
                  dayGroups={paginatedDayGroups}
                  allExpanded={allExpanded}
                  onAllExpandedChange={setAllExpanded}
                  onRefundSuccess={refreshPixForActiveAccount}
                />
                <div className="mt-4">
                  <GridPagination
                    total={totalDayGroups}
                    page={page}
                    rowsPerPage={daysPerPage}
                    currentPageCount={currentPageDayCount}
                    onPageChange={setPage}
                    onRowsPerPageChange={handleDaysPerPageChange}
                    itemLabel={intl.get("common.days")}
                  />
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {canReceivePix ? (
        <PixQrReceiveModal
          open={qrReceiveModal}
          onClose={() => setQrReceiveModal(false)}
        />
      ) : null}
      {canPayPix ? (
        <PixTransferModal
          open={transferModal}
          onClose={({ refetchStatement = false } = {}) => {
            setTransferModal(false);
            if (refetchStatement) {
              refreshPixForActiveAccount();
            }
          }}
        />
      ) : null}
    </div>
  );
};
